import React, { useState, useMemo } from 'react'
import '.././popup.common.css'
import styles from './Style.module.css'
import CustomModal from '../../Modal/CustomModal'
import BorderedButton from '../../Buttons/BorderedButton'
import PrimaryButton from '../../Buttons/PrimaryButton'
import CustomRadio from '../../CustomRadio/CustomRadio'
import DropdownWeeksCount from '../../DropDown/DropdownWeeksCount'
import { useGlobalContext } from '../../../Context/GlobalContext'
import { postRepeatBookingData } from '../../../APICalling/APICalling'
import { toast } from 'react-toastify'
import DatePicker from 'react-datepicker'
import CalendarIcon from '../../Icons/CalendarIcon'
import CloseCircleIcon from '../../Icons/CloseCircleIcon'
import { formatDateToDDMMYYYY } from '../../../Utils/utils'

const RepeatBookingPopup = ({onClose}) => {
    const {selectedBookingId, selectedBookingDate, selectedDate, setIsBookingInfoUpdated } = useGlobalContext();
    const [selected, setSelected] = useState("weekly");
    const [selectWeekCount, setSelectWeekCount] = useState("1 Week");
    const [selectedDates, setSelectedDates] = useState([]);
    const [errorMessage, setErrorMessage] = useState([]);
    const [hasConflicts, setHasConflicts] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const weeksOptions = [
        {text: "1 Week"},
        {text: "2 Weeks"},
        {text: "3 Weeks"},
        {text: "4 Weeks"},
        {text: "5 Weeks"},
        {text: "6 Weeks"},
        {text: "7 Weeks"},
        {text: "8 Weeks"},
        {text: "9 Weeks"},
        {text: "10 Weeks"},
        {text: "11 Weeks"},
        {text: "12 Weeks"},
    ];

    // Handle removing a specific date
    const handleRemoveDate = (dateToRemove) => {
        const updatedDates = selectedDates.filter(
            date => date.getTime() !== dateToRemove.getTime()
        );
        setSelectedDates(updatedDates);
    };

    const ReadOnlyInput = React.forwardRef(({ value, onClick }, ref) => (
        <div 
          ref={ref}
          onClick={onClick}
          className={`${styles.DateInput} ${styles.dateInputContainer} form-control`}
        >
            {selectedDates && selectedDates.length > 0 ? (
                <div className={styles.selectedDatesContainer}>
                    {selectedDates.map((date, index) => (
                        <span key={`${date.getTime()}-${index}`} className={styles.dateChip}>
                            <span className={styles.dateText}>{formatDateToDDMMYYYY(date)}</span>
                            <button
                                type="button"
                                className={styles.deleteDateButton}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveDate(date);
                                }}
                                aria-label="Remove date"
                            >
                                <CloseCircleIcon size={14} />
                            </button>
                        </span>
                    ))}
                </div>
            ) : (
                <span className={styles.placeholderText}>Select dates</span>
            )}
        </div>
      ));
    ReadOnlyInput.displayName = 'ReadOnlyInput';



    const handleChange = (e) => {
        setSelected(e.target.value);
        setErrorMessage([]);
        setSelectedDates([]);
        setHasConflicts(false);
    };



    const selectWeeksCount = (selected) => {
		if (selected === "Select week") {
			setSelectWeekCount("1 Week");
		} else {
			setSelectWeekCount(selected)
		}
	};


    const MAX_SELECTABLE_DATES = 12;

    const handleDateChange = (dates) => {
        if (dates && dates.length > MAX_SELECTABLE_DATES) {
            setSelectedDates(dates.slice(0, MAX_SELECTABLE_DATES));
            toast.warning(`Maximum ${MAX_SELECTABLE_DATES} dates can be selected`);
        } else {
            setSelectedDates(dates || []);
        }
    };

    // Extract number of weeks from selectWeekCount (e.g., "1 Week" -> 1, "2 Weeks" -> 2)
    const recurringWeeks = useMemo(() => {
        if (!selectWeekCount) {
            return 1;
        }
        const weeksMatch = selectWeekCount.match(/\d+/);
        return weeksMatch ? parseInt(weeksMatch[0], 10) : 1;
    }, [selectWeekCount]);

    // Calculate recurring end date based on selectedBookingDate and recurringWeeks
    const recurringEndDate = useMemo(() => {
        if (!selectedBookingDate || !recurringWeeks) {
            return "";
        }

        // Parse the date string "DD-MM-YYYY"
        const [day, month, year] = selectedBookingDate.split("-").map(Number);
        const startDate = new Date(year, month - 1, day);

        // Add weeks (7 days per week)
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + (recurringWeeks * 7));

        // Format back as "DD-MM-YYYY"
        const formattedDay = String(endDate.getDate()).padStart(2, "0");
        const formattedMonth = String(endDate.getMonth() + 1).padStart(2, "0");
        const formattedYear = endDate.getFullYear();

        return `${formattedDay}-${formattedMonth}-${formattedYear}`;
    }, [selectedBookingDate, recurringWeeks]);

    // Calculate minimum date based on selectedDate (day after selectedDate)
    const minDate = useMemo(() => {
        if (!selectedDate) {
            // Fallback to tomorrow if selectedDate is not available
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            tomorrow.setHours(0, 0, 0, 0);
            return tomorrow;
        }
        const nextDate = new Date(selectedDate);
        nextDate.setDate(nextDate.getDate() + 1);
        nextDate.setHours(0, 0, 0, 0);
        return nextDate;
    }, [selectedDate]);
    

    let paramsData = {};
    if(selected === "weekly") {
        paramsData = {
            panel: "outlet",
            booking_id: selectedBookingId,
            recurring_weeks: 1,
            recurring_end_date: recurringEndDate,
        }
    } else {
        paramsData = {
            panel: "outlet",
            booking_id: selectedBookingId,
            recurring_dates: selectedDates.map(date => formatDateToDDMMYYYY(date)).join(','),
        }
    }

    const handleSave = async () => {
        setIsProcessing(true);
        const paramsDataUpdated = {
            ...paramsData,
            check_availability: true,
            force: false,
        }
        try {
            const response = await postRepeatBookingData(paramsDataUpdated);
            if (response?.data?.success) {
                if (response?.data?.data?.has_conflicts) {
                    setErrorMessage(response?.data?.data?.summary_messages);
                    setHasConflicts(true);
                    setIsProcessing(false);
                } else {
                    const response = await postRepeatBookingData(paramsData);
                    if (response?.data?.success) {
                        onClose();
                        toast.success(response?.data?.message);
                        setIsBookingInfoUpdated(true);
                    }
                    setIsProcessing(false);
                }
                // Handle success - close popup or show success message

            } else {
                // Handle error response
                console.error("Error creating repeat booking:", response?.message || "Unknown error");
                setIsProcessing(false);
            }
        } catch (error) {
            console.error("Error calling postRepeatBookingData:", error);
            setIsProcessing(false);
        }
    };

    const handleSaveAnyway = async () => {
        setIsProcessing(true);
        try {
            const response = await postRepeatBookingData(paramsData);
            if (response?.data?.success) {
                onClose();
                toast.success(response?.data?.message);
                setIsBookingInfoUpdated(true);
            }
            setIsProcessing(false);
        } catch (error) {
            console.error("Error calling postRepeatBookingData:", error);
            setIsProcessing(false);
        }
    };
    

  return (

    <CustomModal
        show={true}
        onHide={onClose}
        hideCloseButton={true}
    >
        <div className={`popup ${styles.repeatBookingPopup}`}>	
            <div className={`${styles.header} header`}>
                <h2>Repeating Appointment</h2>
            </div>
            <div className="body">
                <div className={styles.row}>
                    <div className={styles.col}>
                        <CustomRadio
                            label="Weekly"
                            name="customRadio"
                            value="weekly"
                            checked={selected === "weekly"}
                            onChange={handleChange}
                        />
                    </div>
                    <div className={styles.col}>
                        <CustomRadio
                            label="By Date"
                            name="customRadio"
                            value="byDate"
                            checked={selected === "byDate"}
                            onChange={handleChange}
                        />
                    </div>
                </div>
                {selected === "weekly" ? 
                (
                    <div className={styles.row}>
                        <div className={styles.weekCount}>
                            <label className={styles.weekLabel}>For</label>
                            <DropdownWeeksCount
                                options={weeksOptions}
                                selectedLabel={selectWeekCount}
                                onSelect={selectWeeksCount}
                                dropdownClass={`${styles.dropdownNoBg} dropdownNoBg position-relative w-100`}
                                dropdownToggleClass={`w-100 ${styles.weekCountDropdownToggle}`}
                            />
                        </div>
                        <div className={styles.weekCount}>
                            <label className={styles.weekLabel}>Till</label>
                            <div>{recurringEndDate}</div>
                        </div>
                    </div>
                ) 
                : 
                (
                    <div className={styles.row}>
                        <div className={styles.col}>
                            <div className={`${styles.datePickerWrapper} repeat-booking-date-picker-wrapper`}>
                                <label className={styles.dateLabel}>Select dates (max 12)</label>
                                <DatePicker
                                    selectedDates={selectedDates}
                                    selectsMultiple
                                    onChange={handleDateChange}
                                    shouldCloseOnSelect={false}
                                    disabledKeyboardNavigation
                                    calendarClassName={styles.customDatePicker}
                                    placeholderText="Select dates"
                                    dateFormat="dd MMM yyyy"
                                    customInput={<ReadOnlyInput />}
                                    icon={<CalendarIcon />}
                                    minDate={minDate}
                                /> 
                            </div>
                        </div>
                    </div>
                )
                }
                

            </div>
            {
                errorMessage.length > 0 && (
                    <div className={styles.errorMessage}>
                        {errorMessage.map((error, index) => (
                            <div key={index}>
                                <p>{error.message}</p>
                                {error.conflict_dates && error.conflict_dates.length > 0 && (
                                    <p className={styles.conflictDates}>
                                        Conflict dates: {error.conflict_dates.join(', ')}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                )
            }
            <div className={styles.space} />
            <div className="footer">
                <BorderedButton
                    text="Cancel"
                    onClick={onClose}
                />
                {hasConflicts ? (
                    <PrimaryButton
                        text={isProcessing ? "Processing..." : "Save Anyway"}
                        onClick={handleSaveAnyway}
                        disabled={isProcessing}
                    />
                ):
                (
                    <PrimaryButton
                        text={isProcessing ? "Processing..." : "Save"}
                        onClick={handleSave}
                        disabled={isProcessing}
                    />
                )
                }
                
            </div>
        </div>
  </CustomModal>
  )
}

export default RepeatBookingPopup